import { matchers } from 'millan';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import {
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import {
  generateQualifiedTableName,
  getSqlQuotingCharacters,
} from 'src/utils/generateQualifiedTableName';

const VALID_REPLACE_CHAR = '_';
const VARNAME_FIRST_CHAR = new RegExp(`(${matchers.PN_CHARS_U.source})|\\d`);
const VARNAME_OTHER_CHAR = new RegExp(
  `(${VARNAME_FIRST_CHAR.source})|(\\u00B7|[\\u0300-\\u036f]|[\\u203f-\\u2040])`
);

const setCharAt = (str: string, idx: number, char: string) => {
  if (idx > str.length - 1 || idx < 0) {
    return str;
  }
  return str.substring(0, idx) + char + str.substring(idx + 1);
};

export const maybeGetAliasesByColumnName = (
  fields: ProjectVirtualGraphField[]
): Record<string, string> | null => {
  const aliasesByColumnName: Record<string, string> = {};
  let isAColumnAliased = false;

  fields.forEach((field) => {
    const { value } = field;
    let fixedValue = escapeDoubleQuotes(value);
    Array.from(value).forEach((char, idx) => {
      const exp = idx === 0 ? VARNAME_FIRST_CHAR : VARNAME_OTHER_CHAR;
      const matched = char.match(exp);
      if (!matched) {
        fixedValue = setCharAt(fixedValue, idx, VALID_REPLACE_CHAR);
      }
    });

    if (value !== fixedValue) {
      isAColumnAliased = true;
    }
    aliasesByColumnName[value] = fixedValue;
  });

  if (!isAColumnAliased) {
    return null;
  }

  return aliasesByColumnName;
};

export const getSelectStatementWithAliasedColumns = (
  fields: ProjectVirtualGraphField[],
  aliasByColumnName: Record<string, string>,
  nativeQuoteCharacter: string | null,
  options: ProjectVirtualGraphOptions
) => {
  const { openQuote, closeQuote } = getSqlQuotingCharacters({
    nativeQuoteCharacter,
    options,
  });
  const columns = fields.map((field) => {
    const fieldValue = field.value;
    const alias = aliasByColumnName[fieldValue];
    if (fieldValue === alias) {
      return fieldValue;
    }
    return `${openQuote}${escapeDoubleQuotes(
      fieldValue
    )}${closeQuote} AS ${alias}`;
  });

  return `SELECT ${columns.join(', ')}`;
};

export const generateSqlQueryForMapping = ({
  fields,
  nativeQuoteCharacter,
  options,
  table,
}: {
  fields: ProjectVirtualGraphField[];
  nativeQuoteCharacter: string | null;
  options: ProjectVirtualGraphOptions;
  table: DataSourceTable;
}) => {
  const qualifiedTableName = generateQualifiedTableName({
    nativeQuoteCharacter,
    options,
    table,
  });

  const aliasesByColumnName = maybeGetAliasesByColumnName(fields);

  const select = aliasesByColumnName
    ? getSelectStatementWithAliasedColumns(
        fields,
        aliasesByColumnName,
        nativeQuoteCharacter,
        options
      )
    : 'SELECT *';
  const from = `FROM ${qualifiedTableName}`;

  return { aliasesByColumnName, query: `${select} ${from}` };
};
